import { ReactNode, useEffect } from 'react';
import styled from 'styled-components';

import { ErrorBoundary } from '@sorare/error-boundary';

import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

const Root = styled.section`
  display: flex;
  isolation: isolate;
  flex-direction: column;
  height: var(--100vh);
  overflow: hidden;
  position: relative;
  background-color: var(--c-black);
  color: var(--c-white);
`;
const ContentWithDetails = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
`;
const Content = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
  flex: 1;
  max-width: 100%;
`;
const ScrollableContent = styled.div`
  flex: 1;
  overflow: auto;
  isolation: isolate;
`;
const DrawerWrapper = styled.div`
  overflow: auto;
  height: 100%;
  box-shadow: 0 0 var(--quadruple-unit) rgba(var(--c-rgb-black), 0.03);
`;
const StickyWrapper = styled.div<{ showBackButton: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1;
`;
const FiltersWrapper = styled(Container)`
  background-color: var(--c-black);
  padding-bottom: var(--unit);
`;
const ContainerWrapper = styled(Container)`
  overflow: hidden;
  @media ${tabletAndAbove} {
    row-gap: var(--quadruple-unit);
  }
  @media ${laptopAndAbove} {
    padding: 0 calc(50px + var(--unit)) calc(50px + var(--unit));
  }
`;

export const PAGE_SIZE = 20;

type Props = {
  onLoad?: () => void;
  header?: ReactNode;
  filters?: ReactNode;
  body?: ReactNode;
  positionPicker?: ReactNode;
  drawer?: ReactNode;
  scrollRef?: React.RefObject<HTMLDivElement>;
};

export const DumbDraft = ({
  onLoad,
  header,
  filters,
  body,
  positionPicker,
  drawer,
  scrollRef,
}: Props) => {
  // We want this callback to be called only once
  useEffect(() => {
    onLoad?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorBoundary fallback={null}>
      <Root>
        <ContentWithDetails>
          <Content>
            <ScrollableContent ref={scrollRef}>
              <StickyWrapper showBackButton={false}>
                {header}
                <FiltersWrapper>{filters}</FiltersWrapper>
              </StickyWrapper>
              <ContainerWrapper>{body}</ContainerWrapper>
            </ScrollableContent>
            {positionPicker}
          </Content>
          <DrawerWrapper>{drawer}</DrawerWrapper>
        </ContentWithDetails>
      </Root>
    </ErrorBoundary>
  );
};
